import { Box, Group, Radio, Select, Text, Textarea, TextInput, Title, UnstyledButton } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import { useSelector } from "react-redux"
import useSWR from "swr"
import { BranchApi, GovernanceConfigApi, StaffApi } from "../../../apis"
import type { IDateAvailability } from "../../../apis/governance-config"
import type { IAuth } from "../../../store/Auth"
import type { CartItem } from "../../../store/Cart"
import type { IGeneral } from "../../../store/General"
import { currencyFormat } from "../../../utils/currency"
import { renderName } from "../../../utils/render-name"
import { DotPill } from "../../delivery/atoms"
import { PermissionButton } from "../../ui"
import {
  advancePercentOf,
  checkoutErrorText,
  discountValueText,
  extraDiscountOf,
  groupCart,
  groupVatAfterDiscount,
  inactiveNetworkReason,
  isConsignmentGroup,
  networkCanOrder,
  promoPricedCount,
  promotionSummaryText,
  resolveTermId,
  submitCart,
  supplierCode,
  termDiscountAmount,
  termOptions,
  useCartBreakdowns,
  useCartPreviews,
  useSupplierNetworks,
  zeroPriceItems,
  type CartPreviews,
  type CheckoutResult,
} from "./cart-model"
import classes from "./CartDrawer.module.css"
import { CartTotals } from "./CartTotals"
import type { SupplierCartGroup } from "./types"
import { useFinishCheckout } from "./use-finish-checkout"
import { cleanName } from "./utils"

type SupplierChoice = { deliveryType: string; deliveryDate: string; paymentTermId: string }

const NOTE_MAX = 255
const DAYS_SHOWN = 7
const EMPTY_CHOICE: SupplierChoice = { deliveryType: "DEFAULT_DATE", deliveryDate: "", paymentTermId: "" }

type Props = {
  items: CartItem[]
  /** Rendered inside the cart drawer: one column, actions in the drawer foot. */
  compact?: boolean
  /** Compact only: shown first in the scrolling column (the cart lines). */
  header?: React.ReactNode
  /** Compact only: a quiet action under the lines (save as list). */
  footerExtra?: React.ReactNode
  /** Called once an order went through (the success page is opening). */
  onSubmitted?: () => void
}

/**
 * Checkout page body (canvas OrdB_Checkout, OrdB_PromoCheckout; B01 B09 B29 B06).
 * Receiver once for the whole cart; delivery, payment term and advance per
 * supplier, because every supplier becomes its own order and its own request.
 */
export function CartCheckout({ items, compact = false, header, footerExtra, onSubmitted }: Props) {
  const { t } = useTranslation(["order", "common"])
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)
  const { deliveryDateTypes } = useSelector((state: { general: IGeneral }) => state.general)
  const finish = useFinishCheckout()

  const groups = React.useMemo(() => groupCart(items), [items])
  const supplierIds = Object.keys(groups)
  const { networks, isLoading: networksLoading } = useSupplierNetworks(supplierIds)
  const breakdowns = useCartBreakdowns(items, user?.currentBusinessId || null)

  const [receiverBranchId, setReceiverBranchId] = React.useState("")
  const [receiverStaffId, setReceiverStaffId] = React.useState("")
  const [senderNote, setSenderNote] = React.useState("")
  const [senderAdditionalNote, setSenderAdditionalNote] = React.useState("")
  const [choices, setChoices] = React.useState<Record<string, SupplierChoice>>({})
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [failures, setFailures] = React.useState<CheckoutResult[]>([])
  const [submitting, setSubmitting] = React.useState<"send" | "save" | null>(null)

  const choiceOf = (id: string): SupplierChoice => choices[id] ?? EMPTY_CHOICE
  const setChoice = (id: string, patch: Partial<SupplierChoice>) => {
    setChoices((prev) => ({ ...prev, [id]: { ...(prev[id] ?? EMPTY_CHOICE), ...patch } }))
    setErrors((prev) => ({ ...prev, [`${id}.deliveryDate`]: "", [`${id}.term`]: "" }))
  }

  const { data: staffData } = useSWR(user?.currentBusinessId ? ["checkout.staff", user.currentBusinessId] : null, async () => {
    try {
      return await StaffApi.select({ businessId: user!.currentBusinessId! })
    } catch {
      return []
    }
  })
  const { data: branchData } = useSWR(user?.currentBusinessId ? ["checkout.branch", user.currentBusinessId] : null, async () => {
    try {
      return await BranchApi.select({ businessId: user!.currentBusinessId })
    } catch {
      return []
    }
  })

  // Pre-fill the receiver from the first supplier's network (its receiver branch and staff).
  const firstNetwork = networks[supplierIds[0]]
  React.useEffect(() => {
    if (!firstNetwork) return
    if (!receiverBranchId && firstNetwork.receiverBranchId) setReceiverBranchId(firstNetwork.receiverBranchId)
    if (!receiverStaffId && firstNetwork.receiverStaff?.id) setReceiverStaffId(firstNetwork.receiverStaff.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstNetwork])

  // Preselect the network's default payment term through the same state a click sets.
  React.useEffect(() => {
    setChoices((prev) => {
      let next = prev
      supplierIds.forEach((id) => {
        const network = networks[id]
        const defaultId = network?.paymentTermId
        if (!defaultId || prev[id]?.paymentTermId || isConsignmentGroup(groups[id])) return
        if (!termOptions(network).some((o) => o.id === defaultId)) return
        next = { ...next, [id]: { ...(prev[id] ?? EMPTY_CHOICE), paymentTermId: defaultId } }
      })
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networks])

  // The network's own receiver is offered even when the select lists leave it out.
  const withDefault = (rows: any[], extra: any, label: (x: any) => string) =>
    [...rows, ...(extra?.id && !rows.some((r) => r?.id === extra.id) ? [extra] : [])].map((x) => ({ value: x.id, label: label(x) || "-" }))
  const branchOptions = withDefault(branchData || [], firstNetwork?.receiverBranch, (b) => (b?.branchAddress ? `${b?.name} - ${b.branchAddress}` : b?.name))
  const staffOptions = withDefault(staffData || [], firstNetwork?.receiverStaff, (s) => renderName(s))
  const deliveryTypeOptions = (deliveryDateTypes || []).map((d: any) => ({ value: d.code, label: d.name }))

  // ── Amounts per supplier (B29: the discount of the term actually picked) ──
  const termOf = (id: string) => resolveTermId(networks[id], choiceOf(id).paymentTermId)
  const discountOf = (id: string) =>
    isConsignmentGroup(groups[id]) ? 0 : termDiscountAmount(networks[id], termOf(id), groups[id].totalAmount).amount

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const pieces = items.reduce((sum, item) => sum + item.quantity, 0)
  const termDiscountTotal = supplierIds.reduce((sum, id) => sum + discountOf(id), 0)
  // The server spreads each supplier's term discount over its lines and extracts
  // the VAT from what is left, so the VAT shrinks with the discount (B29).
  // D3: order create adds the bundle / threshold discount to the order discount (gift lines are 0₮).
  const previews = useCartPreviews(groups)
  const promoOf = (id: string) => extraDiscountOf(previews, id, groups[id].totalAmount)
  const promoDiscountTotal = supplierIds.reduce((sum, id) => sum + promoOf(id), 0)
  const vat = supplierIds.reduce((sum, id) => sum + groupVatAfterDiscount(groups[id], discountOf(id) + promoOf(id)), 0)
  const promoPriced = promoPricedCount(items, breakdowns)
  const zeroLines = zeroPriceItems(items)

  const termDiscountLabel = (() => {
    const picked = supplierIds
      .map((id) => termDiscountAmount(networks[id], termOf(id), groups[id].totalAmount).discount)
      .filter((d): d is NonNullable<typeof d> => !!d?.discountValue)
    const suffix = supplierIds.length === 1 && picked[0] ? ` ${discountValueText(picked[0])}` : ""
    return `${t("order:cart.checkout.termDiscountRow")}${suffix}`
  })()

  // B40: an inactive network takes no orders, sent or saved.
  const inactiveReason = networksLoading ? null : inactiveNetworkReason(t, supplierIds, networks, groups)
  const sendReason = networksLoading
    ? t("order:cart.reasonDraftLoading")
    : inactiveReason
      ? inactiveReason
      : zeroLines.length > 0
        ? t("order:cart.checkout.reasonZeroPrice", { names: zeroLines.map((i) => cleanName(i.nameMon) || i.skuCode).join(", ") })
        : null
  const saveReason = networksLoading ? t("order:cart.reasonDraftLoading") : inactiveReason

  const validate = (send: boolean): boolean => {
    const errs: Record<string, string> = {}
    if (!receiverBranchId) errs.receiverBranchId = t("common:validation.selectRequired")
    if (!receiverStaffId) errs.receiverStaffId = t("order:cart.checkout.receiverStaffRequired")
    supplierIds.forEach((id) => {
      const choice = choiceOf(id)
      const network = networks[id]
      const consignment = isConsignmentGroup(groups[id])
      const needsDate = send || consignment || choice.deliveryType === "CUSTOM_DATE"
      if (needsDate && !choice.deliveryDate) errs[`${id}.deliveryDate`] = t("order:cart.checkout.deliveryDateRequired")
      if (!networkCanOrder(network)) errs[`${id}.network`] = t("order:buyerCustomer.cannotOrderInactive")
      else if (!network?.supplierStaff?.id) errs[`${id}.network`] = t("order:productList.checkoutSupplierManagerError", { name: groups[id].supplierName })
      else if (!network?.buyerStaff?.id) errs[`${id}.network`] = t("order:productList.checkoutBuyerManagerError", { name: groups[id].supplierName })
      if (!consignment) {
        const options = termOptions(network)
        if (options.length === 0) errs[`${id}.term`] = t("order:cart.checkout.termMissing")
        else if (send && !termOf(id)) errs[`${id}.term`] = t("order:cart.checkout.termRequired")
      }
    })
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (send: boolean) => {
    if (submitting) return
    if (send ? sendReason : saveReason) return
    if (!validate(send)) return
    setSubmitting(send ? "send" : "save")
    setFailures([])
    try {
      const results = await submitCart({
        groups,
        networks,
        suppliers: supplierIds.map((id) => ({
          supplierId: id,
          deliveryType: choiceOf(id).deliveryType || "DEFAULT_DATE",
          deliveryDate: choiceOf(id).deliveryDate,
          paymentTermId: termOf(id) || networks[id]?.paymentTermId || "",
        })),
        receiverBranchId,
        receiverStaffId,
        senderNote,
        senderAdditionalNote,
        send,
        user,
      })
      const failed = results.filter((r) => !r.orderId)
      if (finish(send, results, groups)) onSubmitted?.()
      else setFailures(failed)
    } finally {
      setSubmitting(null)
    }
  }

  const grandTotal = Math.max(0, subtotal - termDiscountTotal - promoDiscountTotal)

  const sections = (
    <>
      {/* 1. Receiving and delivery */}
      <Box component="section" className={`${classes.card} ${classes.cardPad}`} aria-labelledby="checkout-receive">
        <Title order={3} id="checkout-receive" className={classes.cardTitle} mb="md">
          {t("order:cart.checkout.receiveSection")}
          <HelpIcon helpKey="order.buyer-sales-checkout.section.receive" variant="section" />
        </Title>
        <Box className={classes.fieldGrid}>
          <Select
            label={t("order:cart.checkout.receiverBranch")}
            placeholder={t("order:cart.checkout.receiverBranchPlaceholder")}
            description={t("order:cart.checkout.receiverBranchHint")}
            inputWrapperOrder={["label", "input", "description", "error"]}
            data={branchOptions}
            value={receiverBranchId || null}
            onChange={(v) => {
              setReceiverBranchId(v || "")
              setErrors((e) => ({ ...e, receiverBranchId: "" }))
            }}
            error={errors.receiverBranchId}
            searchable
            size="md"
          />
          <Select
            label={t("order:cart.checkout.receiverStaff")}
            placeholder={t("order:cart.checkout.receiverStaffPlaceholder")}
            data={staffOptions}
            value={receiverStaffId || null}
            onChange={(v) => {
              setReceiverStaffId(v || "")
              setErrors((e) => ({ ...e, receiverStaffId: "" }))
            }}
            error={errors.receiverStaffId}
            searchable
            size="md"
          />
        </Box>

        {supplierIds.map((id) => (
          <Box key={id} className={supplierIds.length > 1 ? classes.supplierBlock : undefined} mt="md">
            {supplierIds.length > 1 && (
              <Text className={classes.groupName} mb="sm">
                {groups[id].supplierName}
              </Text>
            )}
            <Box className={classes.fieldGrid}>
              <Select
                label={t("order:cart.checkout.deliveryType")}
                data={deliveryTypeOptions}
                value={choiceOf(id).deliveryType}
                onChange={(v) => setChoice(id, { deliveryType: v || "DEFAULT_DATE", deliveryDate: "" })}
                allowDeselect={false}
                size="md"
              />
            </Box>
            {choiceOf(id).deliveryType === "CUSTOM_DATE" ? (
              <DatePickerInput
                mt="md"
                maw={320}
                label={t("order:cart.checkout.deliveryDate")}
                placeholder={t("order:cart.checkout.deliveryDatePlaceholder")}
                valueFormat="YYYY-MM-DD"
                value={choiceOf(id).deliveryDate || null}
                onChange={(v: string | null) => setChoice(id, { deliveryDate: v || "" })}
                minDate={dayjs().add(1, "day").format("YYYY-MM-DD")}
                error={errors[`${id}.deliveryDate`]}
                size="md"
              />
            ) : (
              <DeliveryDays
                supplierId={id}
                value={choiceOf(id).deliveryDate}
                error={errors[`${id}.deliveryDate`]}
                onChange={(date) => setChoice(id, { deliveryDate: date })}
              />
            )}
            {!networksLoading && !networkCanOrder(networks[id]) ? (
              <Text role="alert" className={classes.sub} c="red.8" mt="xs">
                {t("order:buyerCustomer.cannotOrderInactive")}
              </Text>
            ) : (
              errors[`${id}.network`] && (
                <Text role="alert" className={classes.sub} c="red.8" mt="xs">
                  {errors[`${id}.network`]}
                </Text>
              )
            )}
          </Box>
        ))}
      </Box>

      {/* 2. Payment term per supplier */}
      <Box component="section" className={`${classes.card} ${classes.cardPad}`} aria-labelledby="checkout-payment">
        <Title order={3} id="checkout-payment" className={classes.cardTitle}>
          {t("order:cart.checkout.paymentSection")}
          <HelpIcon helpKey="order.buyer-sales-checkout.section.payment" variant="section" />
        </Title>
        {supplierIds.map((id) => (
          <PaymentTerms
            key={id}
            supplierName={groups[id].supplierName}
            group={groups[id]}
            network={networks[id]}
            loading={networksLoading}
            pickedId={termOf(id)}
            error={errors[`${id}.term`]}
            onPick={(termId) => setChoice(id, { paymentTermId: termId })}
            compact={compact}
            showSupplier={supplierIds.length > 1}
          />
        ))}
      </Box>

      {/* 3. Notes */}
      <Box component="section" className={`${classes.card} ${classes.cardPad}`} aria-labelledby="checkout-note">
        <Title order={3} id="checkout-note" className={classes.cardTitle} mb="md">
          {t("order:cart.checkout.noteSection")}
          <HelpIcon helpKey="order.buyer-sales-checkout.section.note" variant="section" />
        </Title>
        <Box className={classes.fieldGrid}>
          <NoteField
            label={t("order:cart.checkout.note")}
            placeholder={t("order:cart.checkout.notePlaceholder")}
            value={senderNote}
            onChange={setSenderNote}
          />
          <NoteField
            label={t("order:cart.checkout.additionalNote")}
            placeholder={t("order:cart.checkout.additionalNotePlaceholder")}
            value={senderAdditionalNote}
            onChange={setSenderAdditionalNote}
          />
        </Box>
      </Box>
    </>
  )

  const totals = (
    <>
      <Box mt={compact ? 0 : "md"}>
        <CartTotals
          types={items.length}
          pieces={pieces}
          subtotal={subtotal}
          promotionText={promotionSummaryText(t, promoPriced, previews)}
          promoDiscount={promoDiscountTotal}
          termDiscount={termDiscountTotal > 0 ? { label: termDiscountLabel, amount: termDiscountTotal } : null}
          vat={vat}
          total={grandTotal}
          totalLabel={t("order:cart.totalAmount")}
        />
      </Box>

      {supplierIds.length > 1 && (
        <Text className={classes.sub} mt="md">
          {t("order:cart.separateOrders", { count: supplierIds.length })}
        </Text>
      )}

      {failures.length > 0 && (
        <Box role="alert" className={classes.alert} mt="md">
          <Text className={classes.infoName}>{t("order:cart.checkout.failedTitle")}</Text>
          {failures.map((f) => (
            <Text key={f.supplierId} className={classes.sub} mt={4}>
              {f.supplierName}: {checkoutErrorText(t, f.error, items)}
            </Text>
          ))}
        </Box>
      )}
    </>
  )

  const sendButton = (
    <PermissionButton
      data-help-id="checkout-send"
      permission="ORD_SEND"
      size="md"
      fullWidth={!compact}
      mt={compact ? undefined : "md"}
      loading={submitting === "send"}
      disabledReason={sendReason}
      onClick={() => handleSubmit(true)}
    >
      {supplierIds.length > 1
        ? t("order:cart.checkout.sendMultiple", { count: supplierIds.length })
        : t("order:cart.checkout.send")}
    </PermissionButton>
  )

  const saveButton = (
    <PermissionButton
      permission="ORD_SAVE"
      size="md"
      fullWidth={!compact}
      mt={compact ? undefined : "sm"}
      variant="default"
      loading={submitting === "save"}
      disabledReason={saveReason}
      onClick={() => handleSubmit(false)}
    >
      {t("order:cart.saveDraft")}
    </PermissionButton>
  )

  // Cart drawer: one scrolling column (lines, receiver, delivery, term, note, totals)
  // and the actions pinned in the drawer foot.
  if (compact) {
    return (
      <>
        <Box className={classes.drawerScroll}>
          {header}
          {footerExtra && <Box mt={-8}>{footerExtra}</Box>}
          {sections}
          <Box className={`${classes.card} ${classes.cardPad}`}>{totals}</Box>
          {sendReason && <Text className={classes.sub}>{sendReason}</Text>}
        </Box>
        <Box className={classes.drawerFoot}>
          {saveButton}
          <Group gap="lg" wrap="nowrap">
            <Box ta="right">
              <Text className={classes.sub}>{t("order:cart.totalAmount")}</Text>
              <Text className={classes.footTotal}>{currencyFormat(grandTotal)}</Text>
            </Box>
            {sendButton}
          </Group>
        </Box>
      </>
    )
  }

  return (
    <Box className={classes.checkout}>
      <Box className={classes.checkoutMain}>{sections}</Box>

      {/* Summary */}
      <Box component="aside" className={`${classes.card} ${classes.cardPad} ${classes.sticky}`} aria-labelledby="checkout-summary">
        <Title order={3} id="checkout-summary" className={classes.cardTitle} mb="sm">
          {t("order:cart.checkout.summary")}
          <HelpIcon helpKey="order.buyer-sales-checkout.section.summary" variant="section" />
        </Title>
        {supplierIds.map((id) => (
          <SummaryGroup key={id} id={id} name={groups[id].supplierName} code={supplierCode(networks[id])} group={groups[id]} previews={previews} />
        ))}
        {totals}
        {sendButton}
        {sendReason && (
          <Text className={classes.sub} mt="xs">
            {sendReason}
          </Text>
        )}
        {saveButton}
      </Box>
    </Box>
  )
}

// ── Delivery day chips (governance calendar of the supplier) ──

function DeliveryDays({
  supplierId,
  value,
  error,
  onChange,
}: {
  supplierId: string
  value: string
  error?: string
  onChange: (date: string) => void
}) {
  const { t } = useTranslation(["order", "common"])
  const today = dayjs().format("YYYY-MM-DD")
  const { data, error: loadError } = useSWR(["checkout.delivery-dates", supplierId, today], () =>
    GovernanceConfigApi.availableDeliveryDates({ businessId: supplierId, from: today, days: DAYS_SHOWN }),
    { revalidateOnFocus: false },
  )

  // Without the calendar, offer the next days and let the server check the date on send.
  const days: IDateAvailability[] =
    data?.dates?.slice(0, DAYS_SHOWN) ??
    Array.from({ length: DAYS_SHOWN }, (_, i) => ({
      date: dayjs().add(i + 1, "day").format("YYYY-MM-DD"),
      available: true,
      reasonIfUnavailable: null,
    }))
  const weekday = (date: string) => {
    const keys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]
    return t(`common:daysShort.${keys[dayjs(date).day()]}`)
  }
  const status = (d: IDateAvailability) => {
    if (d.available) return t("order:cart.checkout.day.available")
    if (d.date === today) return t("order:cart.checkout.day.today")
    return t(`order:cart.checkout.day.${d.reasonIfUnavailable || "unavailable"}`)
  }

  return (
    <Box mt="md">
      <Text fw={600} fz="var(--nw-fz-body)" mb={6} id={`days-${supplierId}`}>
        {t("order:cart.checkout.deliveryDate")}
      </Text>
      <Box className={classes.dayGrid} role="radiogroup" aria-labelledby={`days-${supplierId}`}>
        {days.map((d) => (
          <UnstyledButton
            key={d.date}
            className={classes.day}
            role="radio"
            aria-checked={value === d.date}
            aria-disabled={!d.available}
            aria-label={`${d.date} ${weekday(d.date)}, ${status(d)}`}
            data-selected={value === d.date || undefined}
            onClick={() => d.available && onChange(d.date)}
          >
            <Text span className={classes.dayDate}>
              {dayjs(d.date).format("MM-DD")}
            </Text>
            <Text span inherit>
              {weekday(d.date)}
            </Text>
            {/* Only a day that cannot be picked says why; «Боломжтой» on every chip was noise. */}
            {!d.available && (
              <Text span inherit>
                {status(d)}
              </Text>
            )}
          </UnstyledButton>
        ))}
      </Box>
      {error && (
        <Text role="alert" className={classes.sub} c="red.8" mt="xs">
          {error}
        </Text>
      )}
      <Text className={classes.sub} mt="xs">
        {loadError ? t("order:cart.checkout.deliveryDatesFailed") : t("order:cart.checkout.deliveryHint")}
      </Text>
    </Box>
  )
}

// ── Payment term cards (B29) and the read-only advance (B09) ──

function PaymentTerms({
  supplierName,
  group,
  network,
  loading,
  pickedId,
  error,
  onPick,
  compact = false,
  showSupplier = true,
}: {
  compact?: boolean
  showSupplier?: boolean
  supplierName: string
  group: SupplierCartGroup
  network: any
  loading: boolean
  pickedId: string
  error?: string
  onPick: (termId: string) => void
}) {
  const { t } = useTranslation("order")
  const consignment = isConsignmentGroup(group)
  const options = termOptions(network)
  const picked = termDiscountAmount(network, pickedId, group.totalAmount)
  const advance = consignment ? null : advancePercentOf(network, pickedId)
  const afterDiscount = group.totalAmount - picked.amount

  return (
    <Box mt="md">
      {showSupplier && (
        <Text className={classes.sub} ta="right" mb="xs">
          {supplierName}
        </Text>
      )}
      {loading ? (
        <Text className={classes.sub}>{t("cart.reasonDraftLoading")}</Text>
      ) : consignment ? (
        <Text className={classes.sub}>{t("cart.checkout.consignmentTerm")}</Text>
      ) : options.length === 0 ? (
        <Text role="alert" className={classes.sub}>
          {t("cart.checkout.termMissing")}
        </Text>
      ) : (
        <>
          <Box className={classes.terms} data-compact={compact || undefined} role="radiogroup" aria-label={`${t("cart.checkout.paymentSection")}: ${supplierName}`}>
            {options.map((o) => {
              const amount = termDiscountAmount(network, o.id, group.totalAmount).amount
              const selected = pickedId === o.id
              const discountText =
                amount > 0 && o.discount
                  ? t("cart.checkout.termDiscount", { value: discountValueText(o.discount), amount: currencyFormat(amount) })
                  : t("cart.checkout.termNoDiscount")
              if (compact) {
                return (
                  <UnstyledButton
                    key={o.id}
                    className={classes.termRow}
                    role="radio"
                    aria-checked={selected}
                    data-selected={selected || undefined}
                    onClick={() => onPick(o.id)}
                  >
                    <Radio color="dark" checked={selected} readOnly tabIndex={-1} aria-hidden size="sm" className={classes.termRadio} />
                    <Box className={classes.termRowBody}>
                      <Text className={classes.termRowTitle}>
                        {o.description || "-"}
                        {o.id === network?.paymentTermId && (
                          <Text span className={classes.termDefaultTag}>
                            {t("cart.checkout.termDefault")}
                          </Text>
                        )}
                      </Text>
                      <Text className={classes.sub} data-discount={amount > 0 || undefined}>
                        {discountText}
                      </Text>
                    </Box>
                    <Text span className={classes.amount}>
                      {currencyFormat(group.totalAmount - amount)}
                    </Text>
                  </UnstyledButton>
                )
              }
              return (
                <UnstyledButton
                  key={o.id}
                  className={classes.term}
                  role="radio"
                  aria-checked={selected}
                  data-selected={selected || undefined}
                  onClick={() => onPick(o.id)}
                >
                  <Box className={classes.termHead}>
                    <Radio color="dark" checked={selected} readOnly tabIndex={-1} aria-hidden size="sm" className={classes.termRadio} />
                    <Text span className={classes.termTitle}>
                      {o.description || "-"}
                    </Text>
                  </Box>
                  {o.id === network?.paymentTermId && (
                    <Text span className={classes.sub}>
                      {t("cart.checkout.termDefault")}
                    </Text>
                  )}
                  <Text span className={classes.sub}>
                    {amount > 0 && o.discount
                      ? t("cart.checkout.termDiscount", { value: discountValueText(o.discount), amount: currencyFormat(amount) })
                      : t("cart.checkout.termNoDiscount")}
                  </Text>
                  {amount > 0 && o.discount && (
                    <Text span className={`${classes.sub} ${classes.mono}`}>
                      {o.discount.discountType === "PERCENTAGE"
                        ? t("cart.checkout.termCalc", {
                            subtotal: currencyFormat(group.totalAmount),
                            pct: o.discount.discountValue,
                            amount: currencyFormat(amount),
                            after: currencyFormat(group.totalAmount - amount),
                          })
                        : t("cart.checkout.termCalcFixed", { amount: currencyFormat(amount) })}
                    </Text>
                  )}
                  <Box className={classes.termFoot}>
                    <Text span className={classes.sub}>
                      {t("cart.checkout.newTotal")}
                    </Text>
                    <Text span className={classes.amount}>
                      {currencyFormat(group.totalAmount - amount)}
                    </Text>
                  </Box>
                </UnstyledButton>
              )
            })}
          </Box>
          {error && (
            <Text role="alert" className={classes.sub} c="red.8" mt="xs">
              {error}
            </Text>
          )}
          {compact ? (
            <Text className={classes.sub} mt="sm">
              {t("cart.checkout.advance")}:{" "}
              <Text span fw={600} c="var(--nw-text)">
                {advance == null
                  ? t("cart.checkout.advanceUnknown")
                  : t("cart.checkout.advanceValue", { percent: advance, amount: currencyFormat((afterDiscount * advance) / 100) })}
              </Text>
            </Text>
          ) : (
          <TextInput
            mt="md"
            maw={480}
            readOnly
            size="md"
            label={t("cart.checkout.advance")}
            description={advance == null ? t("cart.checkout.advanceUnknown") : t("cart.checkout.advanceHint")}
            inputWrapperOrder={["label", "input", "description"]}
            value={
              advance == null
                ? "-"
                : t("cart.checkout.advanceValue", { percent: advance, amount: currencyFormat((afterDiscount * advance) / 100) })
            }
          />
          )}
        </>
      )}
    </Box>
  )
}

function NoteField({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <Box>
      <Textarea
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        maxLength={NOTE_MAX}
        minRows={3}
        autosize
        size="md"
      />
      <Text className={classes.sub} ta="right" mt={4}>
        {value.length} / {NOTE_MAX}
      </Text>
    </Box>
  )
}

function SummaryGroup({
  id,
  name,
  code,
  group,
  previews,
}: {
  id: string
  name: string
  code: string
  group: SupplierCartGroup
  previews: CartPreviews
}) {
  const { t } = useTranslation("order")
  // Gift rows order create will add (display only, never in the payload).
  const gifts = previews[id]?.giftLines || []
  return (
    <Box mt="sm">
      <Text className={classes.groupName}>
        {name}
        {code && (
          <Text span className={`${classes.sub} ${classes.mono}`} ml="xs">
            #{code}
          </Text>
        )}
      </Text>
      {group.items.map((item) => (
        <Box key={item.id} className={classes.summaryLine} mt={6}>
          <Box miw={0}>
            <Text fz="var(--nw-fz-body)" fw={600} lineClamp={1}>
              {cleanName(item.nameMon) || item.skuCode}
            </Text>
            <Text className={`${classes.sub} ${classes.mono}`}>
              {item.quantity} x {currencyFormat(item.price)}
            </Text>
          </Box>
          <Text span className={classes.amount}>
            {currencyFormat(item.price * item.quantity)}
          </Text>
        </Box>
      ))}
      {gifts.map((gift) => {
        const item = group.items.find((i) => i.id === gift.variantId)
        return (
          <Box key={`${gift.promotionId}:${gift.variantId}`} className={classes.summaryLine} mt={6}>
            <Box miw={0}>
              <Box className={classes.giftHead}>
                <Text fz="var(--nw-fz-body)" fw={600} lineClamp={1}>
                  {(item && (cleanName(item.nameMon) || item.skuCode)) || gift.promotionName}
                </Text>
                <DotPill tone="green">{t("cart.giftTag")}</DotPill>
              </Box>
              <Text className={`${classes.sub} ${classes.mono}`}>
                {gift.quantity} x {currencyFormat(0)}
              </Text>
            </Box>
            <Text span className={classes.amount}>
              {currencyFormat(0)}
            </Text>
          </Box>
        )
      })}
    </Box>
  )
}
